import type { FileMeta, Settings, Transfer } from './contract';

const HISTORY_KEY = 'drop.history';
const SETTINGS_KEY = 'drop.settings';
const MAX_HISTORY = 100;

export function loadSettings(): Settings {
  try {
    const value = JSON.parse(
      localStorage.getItem(SETTINGS_KEY) ?? 'null',
    ) as Partial<Settings> | null;
    return {
      sounds: value?.sounds ?? true,
      theme: value?.theme ?? 'system',
      autoAcceptPaired: value?.autoAcceptPaired ?? false,
    };
  } catch {
    return { sounds: true, theme: 'system', autoAcceptPaired: false };
  }
}

export function saveSettings(value: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(value));
  } catch {
    /* private browsing */
  }
}

export function loadHistory(): Transfer[] {
  try {
    const value = JSON.parse(
      localStorage.getItem(HISTORY_KEY) ?? '[]',
    ) as Transfer[];
    return Array.isArray(value) ? value.slice(0, MAX_HISTORY) : [];
  } catch {
    return [];
  }
}

export function saveHistory(value: Transfer[]): void {
  try {
    localStorage.setItem(
      HISTORY_KEY,
      JSON.stringify(
        value
          .filter(
            (transfer) =>
              transfer.state !== 'transferring' && transfer.state !== 'paused',
          )
          .slice(0, MAX_HISTORY),
      ),
    );
  } catch {
    /* private browsing */
  }
}

export interface ReceiveStore {
  transferId: string;
  metas: FileMeta[];
  handles: Array<FileSystemFileHandle | undefined>;
  writers: Array<FileSystemWritableFileStream | undefined>;
  parts: Array<BlobPart[]>;
  offsets: number[];
  writes: Array<Promise<void>>;
}

const receives = new Map<string, ReceiveStore>();

export async function openReceive(
  transferId: string,
  metas: FileMeta[],
): Promise<ReceiveStore> {
  const existing = receives.get(transferId);
  if (existing) return existing;

  const store: ReceiveStore = {
    transferId,
    metas,
    handles: [],
    writers: [],
    parts: metas.map(() => []),
    offsets: metas.map(() => 0),
    writes: metas.map(() => Promise.resolve()),
  };

  try {
    // Storage APIs can stall indefinitely in private/mobile contexts. A relay
    // transfer must not wait for optional disk storage, so fall back to Blob
    // parts promptly when OPFS does not answer at any stage.
    const setup = (async (): Promise<void> => {
      const root = await navigator.storage?.getDirectory?.();
      if (!root) return;
      for (let index = 0; index < metas.length; index++) {
        const handle = await root.getFileHandle(
          `drop-recv-${transferId}-${index}`,
          { create: true },
        );
        const writer = await handle.createWritable({ keepExistingData: false });
        store.handles[index] = handle;
        store.writers[index] = writer;
      }
    })();
    await Promise.race([
      setup,
      new Promise<void>((resolve) => window.setTimeout(resolve, 1_000)),
    ]);
  } catch {
    /* OPFS unavailable; Blob parts are the safe fallback */
  }

  receives.set(transferId, store);
  return store;
}

export function writeReceive(
  store: ReceiveStore,
  fileIndex: number,
  offset: number,
  bytes: Uint8Array,
): Promise<void> {
  const writer = store.writers[fileIndex];
  const write = async (): Promise<void> => {
    if (writer) {
      await writer.write({ type: 'write', position: offset, data: bytes as BufferSource });
    } else {
      store.parts[fileIndex].push(bytes.slice());
    }
    store.offsets[fileIndex] = Math.max(
      store.offsets[fileIndex],
      offset + bytes.byteLength,
    );
  };
  store.writes[fileIndex] = store.writes[fileIndex].then(write);
  return store.writes[fileIndex];
}

export async function finishReceive(store: ReceiveStore): Promise<void> {
  await Promise.all(store.writes);
  for (const writer of store.writers) {
    await writer?.close();
  }
}

export async function receivedFiles(transferId: string): Promise<File[]> {
  const store = receives.get(transferId);
  if (!store) return [];
  await Promise.all(store.writes);
  return Promise.all(
    store.metas.map(async (meta, index) => {
      const file = store.handles[index]
        ? await store.handles[index]!.getFile()
        : new File(store.parts[index], meta.name, {
            type: meta.mime,
            lastModified: Date.now(),
          });
      return new File([file], meta.name, {
        type: meta.mime,
        lastModified: Date.now(),
      });
    }),
  );
}

export function clearStoredHistory(): void {
  try {
    localStorage.removeItem(HISTORY_KEY);
  } catch {
    /* private browsing */
  }
}

export async function cleanStaleOpfs(): Promise<void> {
  try {
    const root = await navigator.storage?.getDirectory?.();
    if (!root) return;
    for await (const [name] of root.entries()) {
      if (name.startsWith('drop-recv-')) {
        const handle = await root.getFileHandle(name);
        const file = await handle.getFile();
        if (Date.now() - file.lastModified > 24 * 60 * 60_000) {
          await root.removeEntry(name);
        }
      }
    }
  } catch {
    /* optional storage */
  }
}
